import {
  CALL_SEARCH_MAX_LENGTH,
  CALL_SEARCH_MAX_WORDS,
  CALL_SEARCH_MIN_LENGTH,
} from '../constants/call-search.constant';
import type { CallSearchValidation } from '../types/call-search.type';

const INDEXABLE_TRIGRAM_WORD = /[\p{L}\p{N}]{3,}/u;

/**
 * Normalizes user-entered Calls search text.
 *
 * @param search - Raw browser input.
 * @returns Trimmed text with internal whitespace collapsed.
 */
export const normalizeCallSearch = (search: string): string => {
  return search.trim().replace(/\s+/g, ' ');
};

/**
 * Determines whether PostgreSQL can extract a word trigram from an unbounded
 * substring-search anchor.
 *
 * @param searchWord - One normalized whitespace-delimited search word.
 * @returns True when the word contains three consecutive Unicode letters or
 * digits.
 */
export const isCallSearchIndexableWord = (searchWord: string): boolean => {
  return INDEXABLE_TRIGRAM_WORD.test(searchWord);
};

/**
 * Applies the browser-side half of the bounded call-search contract.
 *
 * @param search - Raw browser input.
 * @returns Normalized validation state used to gate Calls requests.
 */
export const validateCallSearch = (search: string): CallSearchValidation => {
  const normalizedSearch = normalizeCallSearch(search);
  if (!normalizedSearch) {
    return {
      normalizedSearch: '',
      isValid: true,
    };
  }

  if (normalizedSearch.length < CALL_SEARCH_MIN_LENGTH) {
    return {
      normalizedSearch,
      isValid: false,
      error: 'too-short',
    };
  }

  if (normalizedSearch.length > CALL_SEARCH_MAX_LENGTH) {
    return {
      normalizedSearch,
      isValid: false,
      error: 'too-long',
    };
  }

  const words = normalizedSearch.split(' ');
  if (words.length > CALL_SEARCH_MAX_WORDS) {
    return {
      normalizedSearch,
      isValid: false,
      error: 'too-many-words',
    };
  }

  if (!words.some(isCallSearchIndexableWord)) {
    return {
      normalizedSearch,
      isValid: false,
      error: 'no-indexable-word',
    };
  }

  return {
    normalizedSearch,
    isValid: true,
  };
};
